import LinkInOutApp from '@components/LinkInOutApp';
import LoadingContainer from '@components/LoadingContainer';
import OptimizedImage from '@components/OptimizeImage';
import T from '@components/Translate';
import { getNextPage } from '@utils/pagination';
import { removeScripts } from '@utils/helper';
import { TAB_PAGE_CLASS, tabItemClass } from '@styles/classNames';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCategoriesDestination,
  selectDestination,
  selectIsLoading,
} from '../selectors';
import { GET_DESTINATION } from '../slice';

interface Props {
  park: string;
}

const DestinationDiscover = memo(({ park }: Props) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const destination = useSelector(selectDestination);
  const category = useSelector(selectCategoriesDestination);
  const loading = useSelector(selectIsLoading);
  const [themePark, setThemePark] = useState(undefined);

  const handleCate = useCallback(
    (key: string) => {
      if (key === themePark) return;
      setThemePark(key);
      router.push(`/${router.query.park}/diem-den?key=${key}`, null, {
        shallow: true,
      });
    },
    [router, themePark],
  );

  useEffect(() => {
    if (themePark === undefined && router.query.key !== undefined) {
      setThemePark(router.query.key as string);
    }
  }, [router.query.key, themePark]);

  useEffect(() => {
    if (themePark !== undefined) {
      dispatch(
        GET_DESTINATION({
          SiteCode: park,
          key: themePark,
          locale: router.locale,
        }),
      );
    }
  }, [themePark, park, dispatch, router.locale]);

  return (
    <div className="py-20 max-xl:py-15">
      <div className="container-fluid">
        <div className="titleFrame"></div>
        <div className={`${TAB_PAGE_CLASS} mt-8 max-md:mt-5`}>
          <div
            className={tabItemClass(themePark === '')}
            onClick={() => handleCate('')}
          >
            <i className="fa-regular fa-list" />
            <T tKey="   SunParadiseLand.button-all" />
          </div>
          {category?.map(item => (
            <div
              key={item.id}
              className={tabItemClass(item.key === themePark)}
              onClick={() => handleCate(item.key)}
            >
              {item.name} {item.count ? `(${item.count})` : ''}
            </div>
          ))}
        </div>
        <LoadingContainer loading={loading}>
          {destination?.data.length > 0 ? (
            <>
              <div className="mt-8 grid grid-cols-[repeat(2,1fr)] gap-6 max-xl:mt-6 max-xl:grid-cols-[repeat(1,1fr)]">
                {destination?.data.map((item, i) => (
                  <LinkInOutApp
                    href={`/${park}/diem-den/${item?.info?.slug || item?.id}`}
                    className="link group flex overflow-hidden rounded-sm border border-[#0000001a] max-md:flex-col"
                    key={i}
                  >
                    <>
                      <div className="h-[240px] w-[290px] flex-none overflow-hidden max-md:w-full">
                        <OptimizedImage
                          src={item?.info.images[0]?.thumb1000}
                          className="size-full transition-all group-hover:scale-120 max-md:group-hover:scale-100"
                        />
                      </div>
                      <div className="flex grow flex-col px-6 py-5 max-md:p-4">
                        <div className="text-[20px] leading-[normal] font-bold text-black transition-all group-hover:text-accent">
                          {item?.info?.name}
                        </div>
                        {item?.info?.categoryName && (
                          <div className="mt-2 flex items-center gap-1 text-[#919191]">
                            <img
                              src="/static/img/flower.webp"
                              alt=""
                              className="-mt-1 w-4"
                            />
                            {item?.info?.categoryName}
                          </div>
                        )}
                        <article
                          className="mt-3 line-clamp-3 h-[60px] text-[13px] leading-5 text-[#464646]"
                          dangerouslySetInnerHTML={{
                            __html:
                              removeScripts(item?.info?.description) || '',
                          }}
                        />
                      </div>
                    </>
                  </LinkInOutApp>
                ))}
              </div>
              {destination?.data?.length <
                destination?.paginator?.totalCount && (
                <div
                  className="btn btn-2 mx-auto mt-6 h-12 w-max cursor-pointer rounded-md bg-[#1d75701a] px-11 font-semibold text-primary"
                  onClick={() => {
                    dispatch(
                      GET_DESTINATION({
                        SiteCode: park,
                        key: themePark,
                        page: getNextPage(destination?.data?.length),
                        locale: router.locale,
                      }),
                    );
                  }}
                >
                  <T tKey="SunParadiseLand.viewMore" />
                </div>
              )}
            </>
          ) : (
            <div className="noData">
              <T tKey="SunParadiseLand.noData" />
            </div>
          )}
        </LoadingContainer>
      </div>
    </div>
  );
});

export default DestinationDiscover;
